import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import GcPagina from '../Layout/GcPagina';
import { FctEstado, FctIcono, FctSegmentado } from '../../fct/react';
import { listarCatalogo, mensajeDeError, type InformeCatalogo } from '../../api/reportesApi';
import FormularioInforme from './FormularioInforme';

type FiltroEstado = 'activos' | 'todos';

/** Lo que se abre: el formulario de una fila, el de un informe nuevo, o nada. */
type Formulario = { informe: InformeCatalogo | null } | null;

const sinAcentos = (texto: string) => texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const tituloDe = (i: InformeCatalogo) => i.titulo || i.nombre || `Informe ${i.id}, sin nombre`;

/**
 * La gestión del catálogo de informes (#411), en `/reportes`, para quien tiene `Reportes.Gestionar`: todas las filas, con filtro
 * Activos/Todos y búsqueda por título y nombre. Los activos abren el visor en una pestaña nueva; un informe inactivo o sin nombre
 * no se puede abrir, y no lleva enlace. Cada fila da acceso a su formulario.
 */
const CatalogoInformes: React.FC = () => {
  const [informes, setInformes] = useState<InformeCatalogo[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [estado, setEstado] = useState<FiltroEstado>('activos');
  const [texto, setTexto] = useState('');
  const [formulario, setFormulario] = useState<Formulario>(null);

  const cargar = useCallback(() => {
    listarCatalogo()
      .then((leidos) => { setInformes(leidos); setError(null); })
      .catch((e) => setError(mensajeDeError(e, 'No se ha podido cargar el catálogo de informes. Inténtalo de nuevo en unos minutos.')));
  }, []);

  useEffect(cargar, [cargar]);

  const activos = useMemo(() => (informes ?? []).filter((i) => i.activo).length, [informes]);

  const filas = useMemo(() => {
    const buscado = sinAcentos(texto.trim());
    return (informes ?? [])
      .filter((i) => estado === 'todos' || i.activo)
      .filter((i) => !buscado || sinAcentos(`${i.titulo ?? ''} ${i.nombre ?? ''}`).includes(buscado))
      .sort((a, b) => tituloDe(a).localeCompare(tituloDe(b), 'es'));
  }, [informes, estado, texto]);

  const columnas: ColumnsType<InformeCatalogo> = [
    {
      title: 'Estado',
      key: 'estado',
      width: 120,
      render: (_, i) => (
        <FctEstado
          personalizado={i.activo
            ? { etiqueta: 'Activo', familia: 'exito', icono: 'completado' }
            : { etiqueta: 'Inactivo', familia: 'neutro', icono: 'cancelado' }}
        />
      ),
    },
    {
      title: 'Título',
      key: 'titulo',
      render: (_, i) => (i.activo && i.nombre
        ? <Link to={`/reportes/${encodeURIComponent(i.nombre)}`} target="_blank" rel="noopener noreferrer">{tituloDe(i)}</Link>
        : tituloDe(i)),
    },
    { title: 'Nombre', dataIndex: 'nombre', key: 'nombre', render: (nombre: string | null) => <span className="fct-mono">{nombre ?? '—'}</span> },
    { title: 'Responsable', dataIndex: 'responsable', key: 'responsable', render: (responsable: string | null) => responsable ?? '—' },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      render: (_, i) => (
        <Button size="small" icon={<FctIcono nombre="editar" />} aria-label={`Mantenimiento de ${tituloDe(i)}`} onClick={() => setFormulario({ informe: i })}>
          Mantenimiento
        </Button>
      ),
    },
  ];

  return (
    <GcPagina
      seccion="Reportes"
      titulo="Reportes"
      densidad="compacta"
      acciones={<Button icon={<FctIcono nombre="mas" />} onClick={() => setFormulario({ informe: null })}>Nuevo informe</Button>}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 12 }}>
        <FctSegmentado<FiltroEstado>
          etiquetaAccesible="Filtrar por estado"
          valor={estado}
          onCambio={setEstado}
          opciones={[
            { valor: 'activos', etiqueta: 'Activos', contador: activos },
            { valor: 'todos', etiqueta: 'Todos', contador: informes?.length ?? 0 },
          ]}
        />
        <Input
          allowClear
          aria-label="Buscar por título o nombre"
          placeholder="Buscar por título o nombre"
          style={{ maxWidth: 320 }}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
      </div>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      <Table<InformeCatalogo>
        rowKey="id"
        size="small"
        loading={informes === null && !error}
        columns={columnas}
        dataSource={filas}
        scroll={{ x: 760 }}
        pagination={{ pageSize: 50, hideOnSinglePage: true, showSizeChanger: false }}
        locale={{ emptyText: 'Ningún informe coincide con el filtro.' }}
      />
      {formulario && (
        <FormularioInforme
          informe={formulario.informe}
          onCerrar={() => setFormulario(null)}
          onGuardado={() => { setFormulario(null); cargar(); }}
        />
      )}
    </GcPagina>
  );
};

export default CatalogoInformes;
